/**
 * Canonicalizes a hex color for equality comparison: uppercased, expanded to 8 digits (adds FF alpha).
 * v-color-picker (mode="hexa") reformats values (#489d5e -> #489D5EFF).
 * */
export function canonicalHex(c) {
	if (!c) return '';
	let hex = String(c).trim().replace(/^#/, '').toUpperCase();
	if (hex.length === 3 || hex.length === 4) hex = hex.replace(/./g, '$&$&');
	if (hex.length === 6) hex += 'FF';
	return hex;
}

/**
 * displays date in localized string
 * */
export function formatDateWithTime(timestamp) {
	if (!timestamp) return '';

	const newDate = new Date(timestamp),
		militaryHour = newDate.getHours(),
		mm = newDate.getMonth() + 1,
		dd = newDate.getDate(),
		yy = newDate.getFullYear() - 2000,
		hr = militaryHour === 0 ? 12 :
			militaryHour > 12 ? militaryHour - 12 : militaryHour,
		min =
			newDate.getMinutes() < 10
				? `0${newDate.getMinutes()}`
				: newDate.getMinutes(),
		ap = militaryHour > 12 ? "PM" : "AM";

	let date = `${mm}/${dd}/${yy}`;
	let time = `${hr}:${min} ${ap}`;

	return `${date} ${time}`;
}

/**
 * displays shortened date code
 * Will show "just now" for today or "yesterday"
 * Otherwise formats as MM/DD/YY
 *
 * input: date object
 * */
export function formatDate(timestamp) {
	if (!timestamp) return "";

	let today = new Date(),
		date = new Date(timestamp),
		todayMm = today.getMonth() + 1,
		todayDd = today.getDate(),
		todayYy = today.getFullYear() - 2000,
		mm = date.getMonth() + 1,
		dd = date.getDate(),
		yy = date.getFullYear() - 2000;

	if (todayYy === yy && todayMm === mm) {
		let daysSince = todayDd - dd;
		if (daysSince === 0) return "Today";
		if (daysSince === 1) return "Yesterday";
	}

	return `${mm}/${dd}/${yy}`;
}

/**
 * Formats string as a US-style phone number (xxx)xxx-xxxx. 
 * Returns empty string if invalid. 
 * @param {string} value
 */
export function formatPhone(input) {
	if (!input) { return ''; }

	const numbersOnly = input.replace(/\D/g, '');

	if (numbersOnly.length == 0) {
	return numbersOnly;
	} else if (numbersOnly.length <= 3) {
	return numbersOnly.replace(/(\d{1,3})/, '($1');
	} else if (numbersOnly.length > 3 && numbersOnly.length <= 6) {
	return numbersOnly.replace(/(\d{3})(\d{1,3})/, '($1) $2');
	} else {
	return numbersOnly.replace(/(\d{3})(\d{3})(\d{1,4})/, '($1) $2-$3');
	}
}

/**
 * Formats dollars as $#,###.##
 * Set deciamls to true to format as $#,###
 * Will not display decimals for values over $1000
 *
 * @param {float} value
 * @param {bool} decimals
 */
export function formatDollars(value, decimals = true) {
	if (value == null) return null;
	let str = "$";
	if (value < 0) {
		str = "-$";
	}
	var decimalPoints = 0;
	if (decimals) {
		decimalPoints = 2;
	}
	return (str += Math.abs(value)
		.toFixed(decimalPoints)
		.replace(/\B(?=(\d{3})+(?!\d))/g, ","));
}

/**
 * Formats number with delimiter
 * Change second parameter to change default delimiter
 *
 * @param {float} value
 * @param {character} delimiter
 */
export function formatNumber(value, delimiter = ",") {
	if (value == null) return null;
	return Math.abs(value)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
}

/**
 * Formats name. Finds capitalization in the text and adds a space
 * in between lowercase and uppercase character
 *
 * @param {string} str
 */
export function formatName(str) {
	return str.replace(/([A-Z])/g, ' $1').trim();
}

/**
 * Displays time as 'just now', minutes, hours, or days,
 * whichever is most appropriate
 *
 * @param {date} date
 */
export function displayTime(date) {
	if (!date) return "";
	var time = Math.floor((new Date() - date) / 1000 / 60); //minutes
	if (time < 1) return "just now";
	if (time < 60) return time + " min";
	time = Math.floor(time / 60); //hours now
	if (time < 24) return time + " hr";
	time = Math.floor(time / 24); //days now
	return time + " days";
}

/**
 * Add specified months to a date (similar to C# AddMonths method)
 *
 * @param {integer} numOfMonths
 * @param {date} date
 */
export function addMonthsToDate(numOfMonths, date = new Date()) {
	const newMonth = date.getMonth() + numOfMonths;
	date.setMonth(newMonth);
	return date;
}

/**
 * Unslugify any text. Removes any highens in the text and replace them
 * with spaces
 *
 * @param {string} str
 */
export function unslugify(str) {
	return str.replace(/-/g, " ");
}

/**
 * Print DOM element & its contents
 *
 * @param {string} elementId
 */
export function printElement(elementId) {
	var $printContent = document.getElementById(elementId);
	let $printContentParent = $printContent.parentNode;
	var $printContainer = document.createElement("div");

	document.body.appendChild($printContainer);

	$printContainer.setAttribute("id", "printContainer");
	$printContainer.setAttribute("name", "printContainer");

	// Clone popup content to print container
	$printContainer.appendChild($printContent);
	// Print
	window.print();
	// Delete print container
	$printContentParent.appendChild($printContent);
	$printContainer.parentNode.removeChild($printContainer);
}

export function isPastDateWithinDaysFromToday(dateString, daysLimit) {
	const now = new Date();
	const date = new Date(dateString);
	const differenceInDays = (now - date) / (1000 * 60 * 60 * 24);
	return differenceInDays < daysLimit;
}

export function removeTrailingSlash(string) {
	const lastChar = string.charAt(string.length - 1);
	return lastChar === '/' ?
		string.slice(0, -1) :
		string;
}

export function temporaryCorrectTimeOffset(timestamp) {
	const date = new Date(timestamp);
	const hoursToSubtract = 7;

	return new Date(date.getTime() - hoursToSubtract * 60 * 60 * 1000);
}

export function mapAccountStatus(status) {
	switch (status) {
		case 'Locked':
		case 'Apply':
			return 'Paused';
		case 'Deleted':
			return 'Archived';
		case 'Open':
		default:
			return 'Active';
	}
}

export function sortNewestDateFirst(dateStrA, dateStrB) {
	const dateA = new Date(dateStrA),
		dateB = new Date(dateStrB);
	return dateB - dateA;
}

export function sortAlphaAscending(a, b) {
	return a.localeCompare(b);
}

export function sortAlphaDescending(a, b) {
	return b.localeCompare(a);
}

// Mirrors MerchandiseProductsController.MaxImageSizeBytes.
export const MAX_IMAGE_UPLOAD_BYTES = 10 * 1024 * 1024;

// Base64 body is ~1.37x the file vs ~3.57x for a JSON int-array — keeps 10MB under the server limit.
export function fileToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			resolve(result.slice(result.indexOf(',') + 1));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});
}

export function doesZipMatchState(zip, state) {
	// https://www.irs.gov/pub/irs-utl/zip_code_and_state_abbreviations.pdf

	// Slice once, use many times
	var first3 = Number(zip.slice(0, 3));

	switch (state) {
		// States
		case 'AL':
			return ((first3 >= 350 && first3 <= 352) || (first3 >= 354 && first3 <= 369));
		case 'AK':
			return (first3 >= 995 && first3 <= 999);
		case 'AZ':
			return ((first3 >= 850 && first3 <= 853) || (first3 >= 855 && first3 <= 857) || (first3 >= 859 && first3 <= 860) || (first3 >= 863 && first3 <= 865));
		case 'AR':
			return (first3 >= 716 && first3 <= 729);
		case 'CA':
			return ((first3 >= 900 && first3 <= 908) || (first3 >= 910 && first3 <= 928) || (first3 >= 930 && first3 <= 961));
		case 'CO':
			return (first3 >= 800 && first3 <= 816);
		case 'CT':
			return ((first3 >= 60 && zip <= 6389) || (zip >= 6391 && first3 <= 69)); //Leading Zeros have to be removed
		case 'DE':
			return (first3 >= 197 && first3 <= 199);
		case 'DC':
			return (first3 == 200 || (first3 >= 202 && first3 <= 205) || first3 == 569);
		case 'FL':
			return ((first3 >= 320 && first3 <= 339) || first3 == 341 || first3 == 342 || first3 == 344 || first3 == 346 || first3 == 347 || first3 == 349);
		case 'GA':
			return ((first3 >= 300 && first3 <= 319) || first3 == 398 || first3 == 399);
		case 'HI':
			return (first3 == 967 || first3 == 968);
		case 'ID':
			return (first3 >= 832 && first3 <= 838);
		case 'IL':
			return ((first3 >= 600 && first3 <= 620) || (first3 >= 622 && first3 <= 629));
		case 'IN':
			return (first3 >= 460 && first3 <= 479);
		case 'IA':
			return ((first3 >= 500 && first3 <= 516) || (first3 >= 520 && first3 <= 528));
		case 'KS':
			return ((first3 >= 660 && first3 <= 662) || (first3 >= 664 && first3 <= 679));
		case 'KY':
			return (first3 >= 400 && first3 <= 427);
		case 'LA':
			return (first3 == 700 || first3 == 701 || (first3 >= 703 && first3 <= 708) || (first3 >= 710 && first3 <= 714));
		case 'ME':
			return (first3 >= 39 && first3 <= 49); //Leading Zeros have to be removed
		case 'MD':
			return ((first3 >= 206 && first3 <= 212) || (first3 >= 214 && first3 <= 219));
		case 'MA':
			return ((first3 >= 10 && first3 <= 27) || first3 == 55); //Leading Zeros have to be removed
		case 'MI':
			return (first3 >= 480 && first3 <= 499);
		case 'MN':
			return (first3 == 550 || first3 == 551 || (first3 >= 553 && first3 <= 567));
		case 'MS':
			return (first3 >= 386 && first3 <= 397);
		case 'MO':
			return (first3 == 630 || first3 == 631 || (first3 >= 633 && first3 <= 641) || (first3 >= 644 && first3 <= 658));
		case 'MT':
			return (first3 >= 590 && first3 <= 599);
		case 'NE':
			return (first3 == 680 || first3 == 681 || (first3 >= 683 && first3 <= 693));
		case 'NV':
			return ((first3 >= 889 && first3 <= 891) || (first3 >= 893 && first3 <= 895) || first3 == 897 || first3 == 898);
		case 'NH':
			return (first3 >= 30 && first3 <= 38); //Leading Zeros have to be removed
		case 'NJ':
			return (first3 >= 70 && first3 <= 89); //Leading Zeros have to be removed
		case 'NM':
			return (first3 == 870 || first3 == 871 || (first3 >= 873 && first3 <= 875) || (first3 >= 877 && first3 <= 884));
		case 'NY':
			return (first3 == 5 || zip == 6390 || (first3 >= 100 && first3 <= 149)); //Leading Zeros have to be removed
		case 'NC':
			return (first3 >= 270 && first3 <= 289);
		case 'ND':
			return (first3 >= 580 && first3 <= 588);
		case 'OH':
			return (first3 >= 430 && first3 <= 459);
		case 'OK':
			return (first3 == 730 || first3 == 731 || (first3 >= 734 && first3 <= 741) || (first3 >= 743 && first3 <= 749));
		case 'OR':
			return (first3 >= 970 && first3 <= 979);
		case 'PA':
			return (first3 >= 150 && first3 <= 196);
		case 'RI':
			return (first3 == 28 || first3 == 29); //Leading Zeros have to be removed
		case 'SC':
			return (first3 >= 290 && first3 <= 299);
		case 'SD':
			return (first3 >= 570 && first3 <= 577);
		case 'TN':
			return (first3 >= 370 && first3 <= 385);
		case 'TX':
			return (first3 == 733 || (first3 >= 750 && first3 <= 770) || (first3 >= 772 && first3 <= 799));
		case 'UT':
			return (first3 >= 840 && first3 <= 847);
		case 'VT':
			return ((first3 >= 50 && first3 <= 54) || (first3 >= 56 && first3 <= 59)); //Leading Zeros have to be removed
		case 'VA':
			return (first3 == 201 || (first3 >= 220 && first3 <= 246));
		case 'WA':
			return ((first3 >= 980 && first3 <= 986) || (first3 >= 988 && first3 <= 994));
		case 'WV':
			return (first3 >= 247 && first3 <= 268);
		case 'WI':
			return ((first3 >= 530 && first3 <= 532) || first3 == 534 || first3 == 535 || (first3 >= 537 && first3 <= 549));
		case 'WY':
			return ((first3 >= 820 && first3 <= 831) || first3 == 834);

		// Not States
		case 'AS':
			return (zip == 96799);
		case 'FM':
			return (first3 == 969);
		case 'GU':
			return (first3 == 969);
		case 'MH':
			return (first3 == 969);
		case 'MP':
			return (first3 == 969);
		case 'PW':
			return (first3 == 969);
		case 'PR':
			return (first3 == 6 || first3 == 7 || first3 == 9); //Leading Zeros have to be removed
		case 'VI':
			return (first3 == 8); //Leading Zeros have to be removed

		// Military Overseas
		case 'AA':
			return (first3 == 340);
		case 'AE':
			return (first3 >= 90 && first3 <= 98); //Leading Zeros have to be removed
		case 'AP':
			return (first3 >= 962 && first3 <= 966);

		default:
			return false; // Invalid state code
	}
}